import React from 'react';
import { motion } from 'framer-motion';
interface RunwayItem {
  domain: string;
  days: number;
}
const items: RunwayItem[] = [{
  domain: 'legacy.acme.io',
  days: -2
}, {
  domain: 'shop.acme.com',
  days: 9
}, {
  domain: 'store.acme.io',
  days: 17
}, {
  domain: 'api.acme.com',
  days: 41
}, {
  domain: 'acme.com',
  days: 58
}, {
  domain: 'blog.acme.com',
  days: 87
}];
const scale = 90;
const snap = [0.23, 1, 0.32, 1] as const;
function tone(days: number) {
  if (days < 0) {
    return {
      bar: 'bg-critical',
      text: 'text-critical-ink',
      chip: 'bg-critical-soft'
    };
  }
  if (days <= 21) {
    return {
      bar: 'bg-warn',
      text: 'text-warn-ink',
      chip: 'bg-warn-soft'
    };
  }
  return {
    bar: 'bg-healthy',
    text: 'text-healthy-ink',
    chip: 'bg-healthy-soft'
  };
}

/**
 * Days of runway left on each certificate, as a single readable board.
 * The bars draw in on mount, which is what makes the scale legible.
 */
export function ExpiryRunway() {
  return <div className="overflow-hidden rounded-card border border-line bg-surface shadow-raised" data-id="element-312" data-mp="c00dojlef22c">
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5" data-id="element-313" data-mp="2o8r2eug7i4fh">
        <div className="flex items-center gap-2.5" data-id="element-314" data-mp="2jb8qcv6w7939">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-healthy" data-id="element-315" data-mp="2x9oxgn8o31lx" />
          <h2 className="text-[13px] font-semibold text-ink" data-id="element-316" data-mp="2t7lhblb1z8ub">
            Certificate runway
          </h2>
        </div>
        <span className="font-mono text-[11px] text-ink-soft" data-id="element-317" data-mp="376ti2imv6p0m">
          6 monitors · 08:00
        </span>
      </div>

      <div className="relative px-5 py-5" data-id="element-318" data-mp="3278mmi6wkysn">
        <ul className="relative space-y-2" data-id="element-319" data-mp="326fmi02g97jh">
          {items.map((item, index) => {
          const colors = tone(item.days);
          const width = Math.max(2, Math.max(item.days, 0) / scale * 100);
          return <li key={item.domain} className="group flex items-center gap-3 rounded py-1" data-id="element-320" data-mp="3pxb98is1tcjv" data-mpi={index}>
                <span className="w-[104px] shrink-0 truncate font-mono text-xs text-ink-muted transition-colors duration-150 ease-snap group-hover:text-ink sm:w-[124px] sm:text-[13px]" data-id="element-321" data-mp="3l0ch25ry3fob">
                  {item.domain}
                </span>
                <span className="relative h-2.5 flex-1 rounded-full bg-mist-deep" data-id="element-322" data-mp="2rm1qmvegk4ul">
                  <motion.span className={`absolute inset-y-0 left-0 rounded-full ${colors.bar}`} initial={{
                width: 0
              }} animate={{
                width: `${width}%`
              }} transition={{
                duration: 0.28,
                delay: 0.08 + index * 0.05,
                ease: snap
              }} data-id="element-323" data-mp="2rk72773z9ulr" />
                  <span className="absolute -bottom-2 -top-2 left-[23%] w-px bg-warn" aria-hidden="true" data-id="element-324" data-mp="3fbm797n52q0p" />
                </span>
                <span className={`w-[68px] shrink-0 rounded px-1.5 py-0.5 text-right font-mono text-xs font-medium sm:text-[13px] ${colors.chip} ${colors.text}`} data-id="element-325" data-mp="1gychdpnxoj8r">
                  {item.days < 0 ? 'expired' : `${item.days}d`}
                </span>
              </li>;
        })}
        </ul>

        <div className="mt-4 flex items-center gap-3 border-t border-line pt-3" data-id="element-326" data-mp="13nhfhwx81itt">
          <span className="w-[104px] shrink-0 text-[11px] text-ink-soft sm:w-[124px]" data-id="element-327" data-mp="zmxiqugk8h87">
            days remaining
          </span>
          <span className="flex flex-1 justify-between font-mono text-[11px] text-ink-soft" data-id="element-328" data-mp="1wbvae3t0jz15">
            <span data-id="element-329" data-mp="1rewkjxgdue8r">0</span>
            <span data-id="element-330" data-mp="1remte6jlq11h">30</span>
            <span data-id="element-331" data-mp="1rc0ji098n9g3">60</span>
            <span data-id="element-332" data-mp="1nbqfgt50at3v">90</span>
          </span>
          <span className="w-[68px] shrink-0" aria-hidden="true" data-id="element-333" data-mp="1naf1y1lre6zk" />
        </div>
      </div>

      <p className="flex flex-wrap items-center gap-x-2 border-t border-line bg-canvas px-5 py-3.5 text-xs leading-relaxed text-ink-muted" data-id="element-334" data-mp="1icnat9sxuowf">
        <span className="inline-flex items-center gap-1.5" data-id="element-335" data-mp="6y01muo34qh1">
          <span className="h-3 w-px bg-warn" aria-hidden="true" data-id="element-336" data-mp="6wf7h65on9jp" />
          <span className="font-mono text-warn-ink" data-id="element-337" data-mp="1uufx961cj8vd">21d warning line</span>
        </span>
        <span aria-hidden="true" data-id="element-338" data-mp="1zly1q42kvrl3">·</span>
        Two of these six would have gone unnoticed until a visitor hit the
        browser warning.
      </p>
    </div>;
}